import './App.css';
import './Modern.css';
import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import logo from './logo.png';
import HomeSection from './HomeSection';
import CompanySection from './CompanySection';
import ServicesSection from './ServicesSection';
import ContactSection from './ContactSection';
import TermsSection from './TermsSection';

const sections = [
  { id: 'home', title: 'Home' },
  { id: 'company', title: 'Company' },
  { id: 'services', title: 'Services' },
  { id: 'contact', title: 'Contact' },
  { id: 'terms', title: 'Terms' }
];

const resetSectionScroll = id => {
  const section = document.getElementById(id);
  const scroller = section?.querySelector('.section-scroll');
  if (scroller) scroller.scrollTop = 0;
};

function MainContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const initialRouteHandled = useRef(false);
  const previousSection = useRef('home');

  useEffect(() => {
    let frame = 0;

    // Sections can be taller than the viewport on mobile, so track the one under the middle of the screen
    const syncActiveSection = () => {
      frame = 0;
      const marker = window.innerHeight / 2;
      const current = sections.find(({ id }) => {
        const bounds = document.getElementById(id)?.getBoundingClientRect();
        return bounds && bounds.top <= marker && bounds.bottom > marker;
      });

      if (current) setActiveSection(current.id);
    };

    const scheduleSync = () => {
      if (!frame) frame = window.requestAnimationFrame(syncActiveSection);
    };

    syncActiveSection();
    window.addEventListener('scroll', scheduleSync, { passive: true });
    window.addEventListener('resize', scheduleSync);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleSync);
      window.removeEventListener('resize', scheduleSync);
    };
  }, []);

  useEffect(() => {
    const isFirstRoute = !initialRouteHandled.current;
    initialRouteHandled.current = true;

    const id = location.pathname === '/' ? 'home' : location.pathname.slice(1);
    const section = document.getElementById(id);
    if (!section) return;

    const timer = window.setTimeout(() => {
      resetSectionScroll(id);
      section.scrollIntoView({ behavior: isFirstRoute ? 'auto' : 'smooth', block: 'start' });
      setActiveSection(id);
    }, isFirstRoute ? 100 : 0);

    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
    if (previousSection.current === activeSection) return;

    resetSectionScroll(previousSection.current);
    resetSectionScroll(activeSection);
    previousSection.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    document.body.classList.toggle('menu-is-open', menuOpen);
    return () => document.body.classList.remove('menu-is-open');
  }, [menuOpen]);

  const goToSection = (event, id) => {
    event.preventDefault();
    const path = id === 'home' ? '/' : `/${id}`;
    const section = document.getElementById(id);

    resetSectionScroll(id);
    navigate(path);
    setActiveSection(id);
    setMenuOpen(false);
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="site-shell">
      <header className="main-header">
        <Link className="brand" to="/" onClick={event => goToSection(event, 'home')}>
          <img src={logo} alt="GOS Trans" />
          <span className="brand-copy">
            <strong>GOS TRANS</strong>
            <small>Driven by commitment</small>
          </span>
        </Link>

        <nav className={menuOpen ? 'open' : ''} aria-label="Main navigation">
          <ul>
            {sections.map(section => (
              <li key={section.id}>
                <Link
                  to={section.id === 'home' ? '/' : `/${section.id}`}
                  className={activeSection === section.id ? 'active' : ''}
                  onClick={event => goToSection(event, section.id)}
                >
                  {section.title}
                </Link>
              </li>
            ))}
          </ul>
          <a className="nav-cta" href="tel:3126268383">Call dispatch</a>
        </nav>

        <button
          className={`hamburger ${menuOpen ? 'active' : ''}`}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(open => !open)}
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      {menuOpen && <button className="menu-overlay" aria-label="Close menu" onClick={() => setMenuOpen(false)} />}

      <main>
        <HomeSection onNavigate={goToSection} />
        <CompanySection />
        <ServicesSection />
        <ContactSection />
        <TermsSection />
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Router>
      <Routes>
        <Route path="*" element={<MainContent />} />
      </Routes>
    </Router>
  );
}
